import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export enum CoreUIToastDirection {
  topLeft = 'top-left',
  topCenter = 'top-center',
  topRight = 'top-right',
  bottomLeft = 'bottom-left',
  bottomCenter = 'bottom-center',
  bottomRight = 'bottom-right',
}

export class CoreUIToastConfig {
  data?: CoreUIToastData;
}

export class CoreUIToastData {
  title?: string;
  msg?: string;
  msgHtml?: string;
  direction?: CoreUIToastDirection;
  autoClose?: boolean;
  autoCloseTime?: number;
  type?: 'success' | 'error' | 'warning' | 'delete' | 'update' | 'info';
  iconName?: string;
  iconColor?: string;
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  actions?: CoreUIToastAction[];
  closable?: boolean;
  onClose?: () => void;
}

export class CoreUIToastAction {
  label!: string;
  role?: 'primary' | 'secondary' | 'danger' = 'secondary';
  onClick?: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CoreUIToastV2Service {
  private readonly toastSubject = new Subject<CoreUIToastConfig | null>();

  get toasts$(): Observable<CoreUIToastConfig | null> {
    return this.toastSubject.asObservable();
  }

  show(config: CoreUIToastConfig): void {
    if (!config.data) {
      this.toastSubject.next(config);
      return;
    }
    this.toastSubject.next({
      data: { ...config.data, closable: config.data.closable ?? true },
    });
  }

  close(): void {
    this.toastSubject.next(null);
  }

  private createToast(
    type: CoreUIToastData['type'],
    msg: string,
    title: string,
    options?: Partial<CoreUIToastData>,
  ): void {
    this.show({
      data: {
        title,
        msg,
        type,
        autoClose: true,
        autoCloseTime: 5000,
        direction: CoreUIToastDirection.topCenter,
        closable: true,
        ...options,
      },
    });
  }

  success(msg: string, title = 'Operación exitosa', options?: Partial<CoreUIToastData>): void {
    this.createToast('success', msg, title, options);
  }

  error(msg: string, title = 'Ocurrió un error', options?: Partial<CoreUIToastData>): void {
    this.createToast('error', msg, title, options);
  }

  warning(msg: string, title = 'Advertencia', options?: Partial<CoreUIToastData>): void {
    this.createToast('warning', msg, title, options);
  }

  info(msg: string, title = 'Información', options?: Partial<CoreUIToastData>): void {
    this.createToast('info', msg, title, options);
  }

  delete(msg: string, title = 'Eliminado', options?: Partial<CoreUIToastData>): void {
    this.createToast('delete', msg, title, options);
  }

  update(msg: string, title = 'Actualizado', options?: Partial<CoreUIToastData>): void {
    this.createToast('update', msg, title, options);
  }
}
